import { invalidateKeys, queryKeys } from '@/lib/query-keys';
import { apiClient } from '@borradh-workspace/api-client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

interface UseDeleteServiceOptions {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
  /** Off for bulk deletes, which raise one summary toast instead. */
  showSuccessToast?: boolean;
  showErrorToast?: boolean;
}

export const useDeleteService = (options?: UseDeleteServiceOptions) => {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (id: string) => apiClient.delete(`organization-services/${id}`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['organization-services'],
      });
      // The booking payment warning lists services that require payment.
      invalidateKeys(queryClient, queryKeys.integrations.stripeAccountStatus());
      if (options?.showSuccessToast !== false) {
        toast.success('Service deleted');
      }
      options?.onSuccess?.();
    },
    onError: (error: Error) => {
      if (options?.showErrorToast !== false) {
        toast.error(error.message || 'Failed to delete service');
      }
      options?.onError?.(error);
    },
  });

  return {
    deleteService: mutation.mutate,
    deleteServiceAsync: mutation.mutateAsync,
    isDeleting: mutation.isPending,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
  };
};
